import * as React from 'react';
import { searchTagNames } from '../pages/PlaceListPage.logic';
import './PlaceFilterDropdown.css';
import './MultiSelectInput.css';

/** 検索欄に入れた文字から、新しい項目を作れるようにするときの指定 */
export interface MultiSelectCreateOption {
  /** 入れた文字で新しく作れるか。作れるときだけ、リストの最後に作る行を出す */
  canCreate: (query: string) => boolean;
  /** 作る行に出す文言 */
  getLabel: (query: string) => string;
  onCreate: (query: string) => void;
}

interface MultiSelectInputProps<T extends { name: string }> {
  /** 項目名。検索欄の薄い文字と、読み上げに使う */
  label: string;
  /** 何も選んでいないときに、欄の中に薄い文字で出す文言 */
  placeholder: string;
  items: T[];
  getKey: (item: T) => string;
  isSelected: (item: T) => boolean;
  /** 渡した項目をまとめて選ぶ（selected=false なら外す） */
  onSetSelected: (items: T[], selected: boolean) => void;
  createOption?: MultiSelectCreateOption;
}

/** 「すべて選択」の文言。検索で絞り込んでいるときは、見えている項目だけが対象であることを示す */
function getSelectAllLabel(query: string): string {
  if (query.trim() === '') {
    return 'すべて選択';
  }
  return '表示中をすべて選択';
}

/** 欄の枠のクラス。開いているときと押せないときに見た目を変える */
function getFieldClassName(open: boolean, disabled: boolean): string {
  let className = 'MultiSelectInput-field';
  if (open) {
    className += ' open';
  }
  if (disabled) {
    className += ' disabled';
  }
  return className;
}

/**
 * フォームの中で複数の項目を選ぶ欄。選択欄と同じ四角い枠で、選んだ項目を × 付きの札にして枠の中に並べる。
 * 枠を押すと、物件一覧の絞り込みと同じ、検索欄と「すべて選択」の付いたチェックリストが開く。
 * createOption を渡すと、検索欄に入れた文字に当てはまる項目がないときに、その文字で新しく作る行を出す
 */
export function MultiSelectInput<T extends { name: string }>(props: MultiSelectInputProps<T>) {
  const [open, setOpen] = React.useState<boolean>(false);
  const [query, setQuery] = React.useState<string>('');
  const containerRef = React.useRef<HTMLDivElement>(null);

  const closeMenu = React.useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  // メニューの外を押したら閉じる
  React.useEffect(() => {
    if (!open) {
      return;
    }
    const onMouseDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open, closeMenu]);

  const selectedItems = props.items.filter(item => props.isSelected(item));
  const foundItems = searchTagNames(props.items, query);
  const foundSelectedCount = foundItems.filter(item => props.isSelected(item)).length;
  const allFoundSelected = foundItems.length > 0 && foundSelectedCount === foundItems.length;
  const createOption = props.createOption;
  const canCreate = createOption !== undefined && createOption.canCreate(query);
  // 選べる項目も作る手段もないときは、開いても何もできないので押せなくする
  const disabled = props.items.length === 0 && createOption === undefined;

  const toggleOpen = () => {
    if (disabled) {
      return;
    }
    if (open) {
      closeMenu();
      return;
    }
    setOpen(true);
  };

  const create = () => {
    if (!createOption || !canCreate) {
      return;
    }
    createOption.onCreate(query.trim());
    setQuery('');
  };

  let chips;
  if (selectedItems.length === 0) {
    chips = <span className="MultiSelectInput-placeholder">{props.placeholder}</span>;
  } else {
    chips = selectedItems.map(item => (
      <span key={props.getKey(item)} className="MultiSelectInput-chip">
        <span className="MultiSelectInput-chip_label">{item.name}</span>
        <button
          type="button"
          className="MultiSelectInput-chip_remove"
          aria-label={`「${item.name}」を外す`}
          title={`「${item.name}」を外す`}
          onClick={event => {
            // 札の × は、枠を押したときの開閉とは別の操作にする
            event.stopPropagation();
            props.onSetSelected([item], false);
          }}
        >
          ×
        </button>
      </span>
    ));
  }

  let menu;
  if (open) {
    let emptyMessage;
    if (foundItems.length === 0 && !canCreate) {
      emptyMessage = <div className="PlaceFilterDropdown-empty">見つかりません</div>;
    }
    let selectAll;
    if (foundItems.length > 0) {
      selectAll = (
        <label className="PlaceFilterDropdown-item PlaceFilterDropdown-select_all">
          <input
            type="checkbox"
            checked={allFoundSelected}
            ref={input => {
              if (input) {
                input.indeterminate = foundSelectedCount > 0 && !allFoundSelected;
              }
            }}
            onChange={() => props.onSetSelected(foundItems, !allFoundSelected)}
          />
          <span>{getSelectAllLabel(query)}</span>
        </label>
      );
    }
    let createRow;
    if (createOption && canCreate) {
      createRow = (
        <button type="button" className="MultiSelectInput-create" onClick={create}>
          {createOption.getLabel(query.trim())}
        </button>
      );
    }
    menu = (
      <div
        className="PlaceFilterDropdown-menu MultiSelectInput-menu"
        onKeyDown={event => {
          if (event.key === 'Escape') {
            closeMenu();
          }
        }}
      >
        <input
          className="PlaceFilterDropdown-search"
          type="text"
          placeholder={`${props.label}を検索`}
          value={query}
          autoFocus
          onChange={event => setQuery(event.target.value)}
          onKeyDown={event => {
            // 作れる文字が入っているときは、Enterキーでも作る
            if (event.key === 'Enter') {
              event.preventDefault();
              create();
            }
          }}
        />
        <div className="PlaceFilterDropdown-list">
          {emptyMessage}
          {selectAll}
          {foundItems.map(item => (
            <label key={props.getKey(item)} className="PlaceFilterDropdown-item">
              <input
                type="checkbox"
                checked={props.isSelected(item)}
                onChange={() => props.onSetSelected([item], !props.isSelected(item))}
              />
              <span>{item.name}</span>
            </label>
          ))}
          {createRow}
        </div>
      </div>
    );
  }

  return (
    <div className="MultiSelectInput-container" ref={containerRef}>
      {/* 札の × をボタンにするため、枠そのものはボタンにせず、右端の ▾ をキーボードで開くためのボタンにする */}
      <div className={getFieldClassName(open, disabled)} onClick={toggleOpen}>
        <div className="MultiSelectInput-chips">{chips}</div>
        <button
          type="button"
          className="MultiSelectInput-caret"
          aria-label={`${props.label}の選択を開く`}
          aria-expanded={open}
          disabled={disabled}
          onClick={event => {
            event.stopPropagation();
            toggleOpen();
          }}
        >
          ▾
        </button>
      </div>
      {menu}
    </div>
  );
}
